// Tools → Payment warning letters (owner, 2026-09-21). Pick a client, the
// form prefills who the letter is for, what they owe and since when; choose
// one of three levels of strictness, note any bed linen still in the unit,
// and the letter is rendered server-side as ONE A4 page on the company
// letterhead with the signature and stamp. The wording can be edited before
// downloading; only downloaded letters are logged.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import { DUBAI_ZONE, formatDubai, todayDubaiYMD } from '../../../utils/dubaiTime';
import WarningLetterLog, { ArchiveDialog, DeliveryCell, DeliveryDialog, LEVEL_NAMES, daysAgo, downloadLetterAgain } from './WarningLetterLog';
import './warningLetters.css';

const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const plusDays = (days) => DateTime.now().setZone(DUBAI_ZONE).plus({ days }).toFormat('yyyy-MM-dd');
const selectStyles = {
    control: (base, state) => ({
        ...base, minHeight: 40, borderRadius: 8, fontSize: 14,
        borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.15)' : 'none',
        '&:hover': { borderColor: state.isFocused ? '#2563eb' : '#d1d5db' },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 100000 }),
};
const LEVEL_DAYS = { 1: 7, 2: 5, 3: 5 };
const LEVELS = [
    { key: 1, name: 'Payment Reminder', tone: 'First written notice. Polite — assumes an oversight.' },
    { key: 2, name: 'Second Notice', tone: 'Firm. Further recovery steps will follow if it stays unpaid.' },
    { key: 3, name: 'Final Notice', tone: 'Strict. Debt collection agency and Dubai Courts; costs may be added to the bill.' },
];
// An EMPTY remembered value counts as "never set": the field is then filled
// with its default (Manager), not left showing a placeholder. Browsers that
// opened the page while the title still started blank had '' stored.
const remembered = (key, fallback) => { try { return (localStorage.getItem(key) || '').trim() || fallback; } catch (_) { return fallback; } };
const remember = (key, value) => {
    try {
        if (String(value || '').trim()) localStorage.setItem(key, value); else localStorage.removeItem(key);
    } catch (_) { /* private mode */ }
};

async function blobError(err, fallback) {
    const data = err?.response?.data;
    if (data instanceof Blob) {
        try { return JSON.parse(await data.text()).error || fallback; } catch (_) { return fallback; }
    }
    return data?.error || fallback;
}

export default function WarningLetters() {
    const [clients, setClients] = useState([]);
    const [clientOpt, setClientOpt] = useState(null);
    const [prefill, setPrefill] = useState(null);
    const [prefillLoading, setPrefillLoading] = useState(false);

    const [entityKey, setEntityKey] = useState('all');
    const [level, setLevel] = useState(1);
    const [date, setDate] = useState(todayDubaiYMD());
    const [deadline, setDeadline] = useState(plusDays(LEVEL_DAYS[1]));
    const [deadlineTouched, setDeadlineTouched] = useState(false);
    const [amount, setAmount] = useState('');
    const [overdueSince, setOverdueSince] = useState('');
    const [addressee, setAddressee] = useState('');
    const [attention, setAttention] = useState('');
    const [salutation, setSalutation] = useState('Dear Sir/Madam,');
    const [linen, setLinen] = useState({ single: '', double: '', super_king: '' });
    const [withLetterhead, setWithLetterhead] = useState(true);
    const [withSignature, setWithSignature] = useState(true);
    const [includeQr, setIncludeQr] = useState(true);
    const [signatoryName, setSignatoryName] = useState(() => remembered('wl.signatoryName', 'Benjamin Holczer'));
    // Title under the signature: "Manager" (owner, 2026-09-21), editable and remembered per browser.
    const [signatoryTitle, setSignatoryTitle] = useState(() => remembered('wl.signatoryTitle', 'Manager'));

    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [edited, setEdited] = useState(false);
    const [wordingOpen, setWordingOpen] = useState(false);

    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewBusy, setPreviewBusy] = useState(false);
    const [previewError, setPreviewError] = useState('');
    const [downloading, setDownloading] = useState(false);
    const previewSeq = useRef(0);
    // 'new' = write a letter · 'log' = every letter and whether it was delivered
    const [view, setView] = useState('new');
    const [awaiting, setAwaiting] = useState(null);
    const [editingDelivery, setEditingDelivery] = useState(null);
    const [archiving, setArchiving] = useState(null);
    const [marking, setMarking] = useState(false);

    useEffect(() => {
        axios.get('/api/tools/warning-letter/letters', { params: { status: 'pending' } })
            .then((res) => setAwaiting(res.data.pending)).catch(() => {});
    }, []);

    useEffect(() => {
        axios.get('/api/tools/warning-letter/clients')
            .then((res) => setClients(res.data || []))
            .catch(() => toast.error('Could not load the clients'));
    }, []);

    // Found by portal display name, company name or contact name (owner,
    // 2026-09-21). `search` is what the filter reads; the option shows the
    // display name with the company and contact underneath.
    const clientOptions = useMemo(() => clients.map((c) => {
        const contacts = [c.contact_name, c.user_names].filter(Boolean).join(', ');
        const company = c.company_name && c.company_name !== c.display_name ? c.company_name : '';
        return {
            value: c.id,
            label: c.display_name,
            company,
            contacts,
            search: [c.display_name, c.company_name, contacts].filter(Boolean).join(' ').toLowerCase(),
        };
    }), [clients]);
    const filterClient = useCallback((option, input) => {
        const words = String(input || '').toLowerCase().split(/\s+/).filter(Boolean);
        return words.every((w) => option.data.search.includes(w));
    }, []);
    const clientOptionLabel = useCallback((o, { context }) => (context === 'value' ? o.label : (
        <div className="wl__opt">
            <span className="wl__optName">{o.label}</span>
            {(o.company || o.contacts) && <span className="wl__optSub">{[o.company, o.contacts].filter(Boolean).join(' · ')}</span>}
        </div>
    )), []);

    // ── prefill on client change ─────────────────────────────────────────
    const loadPrefill = useCallback(async (clientId) => {
        setPrefillLoading(true);
        setPrefill(null);
        try {
            const res = await axios.get(`/api/tools/warning-letter/prefill/${clientId}`);
            const p = res.data;
            setPrefill(p);
            setEntityKey(p.is_multi_billing ? 'all' : 'default');
            setEdited(false);
            const contact = p.client.contact_person || '';
            setAttention(p.client.is_business ? contact : '');
            setSalutation(contact ? `Dear ${contact},` : (p.client.is_business ? 'Dear Sir/Madam,' : `Dear ${p.client.display_name},`));
        } catch (e) {
            toast.error(e.response?.data?.error || 'Could not load the client’s figures');
        } finally {
            setPrefillLoading(false);
        }
    }, []);

    useEffect(() => { if (clientOpt) loadPrefill(clientOpt.value); }, [clientOpt, loadPrefill]);

    // Open invoices of the chosen billing entity (or all of them), oldest due first.
    const scopeInvoices = useMemo(() => {
        if (!prefill) return [];
        const ents = entityKey === 'all' ? prefill.entities : prefill.entities.filter((e) => e.key === entityKey);
        return ents.flatMap((e) => e.invoices).sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)));
    }, [prefill, entityKey]);
    const overdueInvoices = useMemo(() => scopeInvoices.filter((i) => i.overdue), [scopeInvoices]);
    const overdueTotal = round2(overdueInvoices.reduce((s, i) => s + i.balance, 0));
    const pendingTotal = round2(scopeInvoices.reduce((s, i) => s + i.balance, 0));

    // No selector (owner, 2026-09-21): the letter always asks for every
    // OVERDUE invoice — invoices that are not due yet are not part of a
    // warning. The office can still type its own figure.
    useEffect(() => {
        if (!prefill) return;
        setAmount(overdueTotal > 0 ? overdueTotal.toFixed(2) : '');
        setOverdueSince(overdueInvoices[0]?.due_date || '');
        const ent = prefill.entities.find((e) => e.key === (entityKey === 'all' ? 'default' : entityKey)) || prefill.entities[0];
        setAddressee(ent?.addressee || prefill.client.company_name || prefill.client.display_name || '');
    }, [prefill, entityKey, overdueTotal, overdueInvoices]);

    useEffect(() => { if (!deadlineTouched) setDeadline(plusDays(LEVEL_DAYS[level])); }, [level, deadlineTouched]);
    useEffect(() => { remember('wl.signatoryName', signatoryName); }, [signatoryName]);
    useEffect(() => { remember('wl.signatoryTitle', signatoryTitle); }, [signatoryTitle]);

    const linenNumbers = useMemo(() => ({
        single: Math.max(0, parseInt(linen.single, 10) || 0),
        double: Math.max(0, parseInt(linen.double, 10) || 0),
        super_king: Math.max(0, parseInt(linen.super_king, 10) || 0),
    }), [linen]);
    const linenTotal = useMemo(() => (prefill?.linen_prices || []).reduce((s, l) => s + (linenNumbers[l.key] || 0) * l.price, 0), [prefill, linenNumbers]);

    const amountNum = round2(amount);
    const figures = useMemo(() => ({
        level,
        amount: amountNum,
        overdue_since: overdueSince || null,
        deadline,
        linen: linenNumbers,
        include_qr: includeQr,
        has_pay_link: !!prefill?.has_pay_link,
    }), [level, amountNum, overdueSince, deadline, linenNumbers, includeQr, prefill]);

    // ── standard wording (until edited by hand) ──────────────────────────
    useEffect(() => {
        if (!prefill || edited || !(amountNum > 0)) return undefined;
        const t = setTimeout(() => {
            axios.post('/api/tools/warning-letter/compose', figures)
                .then((res) => { setSubject(res.data.subject); setBody(res.data.body); })
                .catch(() => { /* the preview reports the problem */ });
        }, 250);
        return () => clearTimeout(t);
    }, [prefill, edited, figures, amountNum]);

    const payload = useMemo(() => ({
        ...figures,
        client_id: clientOpt?.value,
        date,
        addressee,
        attention,
        salutation,
        subject,
        body,
        with_letterhead: withLetterhead,
        with_signature: withSignature,
        signatory_name: signatoryName,
        signatory_title: signatoryTitle,
    }), [figures, clientOpt, date, addressee, attention, salutation, subject, body, withLetterhead, withSignature, signatoryName, signatoryTitle]);

    // ── live preview (the real PDF) ──────────────────────────────────────
    useEffect(() => {
        if (!clientOpt || !prefill || !(amountNum > 0) || !body) return undefined;
        const seq = ++previewSeq.current;
        const t = setTimeout(async () => {
            setPreviewBusy(true);
            try {
                const res = await axios.post('/api/tools/warning-letter/render', payload, { responseType: 'blob' });
                if (seq !== previewSeq.current) return;
                const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
                setPreviewError('');
            } catch (e) {
                if (seq !== previewSeq.current) return;
                setPreviewError(await blobError(e, 'Could not generate the letter.'));
            } finally {
                if (seq === previewSeq.current) setPreviewBusy(false);
            }
        }, 700);
        return () => clearTimeout(t);
    }, [payload, clientOpt, prefill, amountNum, body]);

    useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

    const saveBlob = (blob, name) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name.replace(/[\\/:*?"<>|]+/g, ' ');
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
    };
    const refreshHistory = () => axios.get(`/api/tools/warning-letter/prefill/${clientOpt.value}`)
        .then((r) => setPrefill((p) => (p ? { ...p, history: r.data.history } : p))).catch(() => {});

    const refreshAwaiting = () => axios.get('/api/tools/warning-letter/letters', { params: { status: 'pending' } })
        .then((res) => setAwaiting(res.data.pending)).catch(() => {});

    // Download and Print only produce the PDF — NOTHING is logged (owner,
    // 2026-09-21). `print` opens it in a new tab for the printer (the tab is
    // opened before the request so the pop-up blocker lets it through).
    const generate = async ({ print = false } = {}) => {
        const tab = print ? window.open('', '_blank') : null;
        setDownloading(true);
        try {
            const res = await axios.post('/api/tools/warning-letter/render', { ...payload, download: !print }, { responseType: 'blob' });
            const ref = res.headers['x-letter-reference'] || 'warning-letter';
            const blob = new Blob([res.data], { type: 'application/pdf' });
            if (tab) tab.location = URL.createObjectURL(blob);
            else saveBlob(blob, `${ref} ${prefill.client.display_name}.pdf`);
        } catch (e) {
            if (tab) tab.close();
            toast.error(await blobError(e, 'Could not generate the letter.'));
        } finally {
            setDownloading(false);
        }
    };

    // The ONE thing that writes the letter log: the letter is on paper.
    const markPrinted = async () => {
        setMarking(true);
        try {
            const res = await axios.post('/api/tools/warning-letter/letters', payload);
            toast.success(res.data.updated
                ? `${res.data.reference} was already in the log — its entry has been updated`
                : `${res.data.reference} marked as printed — now awaiting delivery in the letter log`);
            refreshHistory();
            refreshAwaiting();
        } catch (e) {
            toast.error(e.response?.data?.error || 'Could not log the letter.');
        } finally {
            setMarking(false);
        }
    };

    const resetWording = () => setEdited(false);
    const ready = !!clientOpt && !!prefill && amountNum > 0 && !!body;
    const nothingOverdue = prefill && overdueInvoices.length === 0;

    return (
        <div className="wl">
            <div className="wl__head">
                <h1 className="wl__title">Payment Warning Letters</h1>
                <p className="wl__sub">A one-page written warning for a client with overdue invoices, on the company letterhead with signature and stamp.</p>
            </div>

            <div className="wl__seg wl__seg--page">
                <button type="button" className={`wl__segBtn${view === 'new' ? ' is-on' : ''}`} onClick={() => setView('new')}>New letter</button>
                <button type="button" className={`wl__segBtn${view === 'log' ? ' is-on' : ''}`} onClick={() => setView('log')}>
                    Letter log{awaiting ? ` · ${awaiting} awaiting delivery` : ''}
                </button>
            </div>

            {view === 'log' && <WarningLetterLog onCountChange={setAwaiting} />}

            <div className="wl__grid" hidden={view !== 'new'}>
                <div className="wl__form">
                    <section className="wl__card">
                        <h2 className="wl__cardTitle">Client</h2>
                        <Select
                            classNamePrefix="wl-sel"
                            options={clientOptions}
                            value={clientOpt}
                            onChange={setClientOpt}
                            filterOption={filterClient}
                            formatOptionLabel={clientOptionLabel}
                            noOptionsMessage={() => 'No client matches'}
                            placeholder="Search by display name, company or contact name…"
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                        {prefillLoading && <div className="wl__muted wl__gapTop">Reading the invoices from Zoho Books…</div>}
                        {prefill?.zoho_error && <div className="wl__warn wl__gapTop">Zoho Books could not be read ({prefill.zoho_error}) — type the figures yourself.</div>}

                        {prefill && (
                            <>
                                {prefill.is_multi_billing && (
                                    <div className="wl__field wl__gapTop">
                                        <label className="wl__label">Billing entity</label>
                                        <Select
                                            classNamePrefix="wl-sel"
                                            isSearchable={false}
                                            options={[{ value: 'all', label: 'All entities together' }, ...prefill.entities.map((e) => ({ value: e.key, label: `${e.label}${e.is_default ? ' (Default)' : ''}` }))]}
                                            value={{ value: entityKey, label: entityKey === 'all' ? 'All entities together' : (prefill.entities.find((e) => e.key === entityKey)?.label || '') }}
                                            onChange={(o) => setEntityKey(o.value)}
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>
                                )}
                                <div className="wl__pending wl__gapTop">
                                    <span className="wl__figureLabel">Overdue invoices ({overdueInvoices.length})</span>
                                    <span className="wl__figureValue">{money(overdueTotal)}</span>
                                    {scopeInvoices.length > overdueInvoices.length && (
                                        <span className="wl__muted">
                                            + {scopeInvoices.length - overdueInvoices.length} not due yet · {money(pendingTotal - overdueTotal)} (not in the letter)
                                        </span>
                                    )}
                                </div>
                                {nothingOverdue && <div className="wl__warn wl__gapTop">This client has no overdue invoices in Zoho Books.</div>}
                                {prefill.history.length > 0 ? (
                                    <div className="wl__history wl__gapTop">
                                        <div className="wl__label">Letters so far</div>
                                        <div className="wl__last">
                                            Last letter: <strong>Level {prefill.history[0].level} {LEVEL_NAMES[prefill.history[0].level]}</strong>, printed {formatDubai(prefill.history[0].generated_at, 'd MMM yy')} ({daysAgo(prefill.history[0].generated_at)})
                                            {prefill.history[0].delivered_on
                                                ? <> — delivered {formatDubai(prefill.history[0].delivered_on, 'd MMM yy')} ({daysAgo(prefill.history[0].delivered_on)})</>
                                                : <> — <span className="wl__notYet">not delivered yet</span></>}
                                        </div>
                                        {prefill.history.map((h) => (
                                            <div className="wl__historyRow" key={h.id}>
                                                <span className={`wl__lvl wl__lvl--${h.level}`}>Level {h.level}</span>
                                                <span>{formatDubai(h.generated_at, 'd MMM')}</span>
                                                <span>{money(h.amount)}</span>
                                                <DeliveryCell compact letter={h} onEdit={(l) => setEditingDelivery({ ...l, client_name: prefill.client.display_name })} />
                                                <span className="wl__rowActions">
                                                    {h.can_download && (
                                                        <button type="button" className="wl__link" onClick={() => downloadLetterAgain(h, prefill.client.display_name)}>Download</button>
                                                    )}
                                                    <button type="button" className="wl__link wl__link--danger" onClick={() => setArchiving({ ...h, client_name: prefill.client.display_name })}>Delete</button>
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="wl__muted wl__gapTop">No warning letter has been marked as printed for this client yet.</div>
                                )}
                            </>
                        )}
                    </section>

                    <section className="wl__card">
                        <h2 className="wl__cardTitle">Level</h2>
                        <div className="wl__levels">
                            {LEVELS.map((l) => (
                                <button
                                    type="button"
                                    key={l.key}
                                    className={`wl__level wl__level--${l.key}${level === l.key ? ' is-on' : ''}`}
                                    onClick={() => setLevel(l.key)}
                                >
                                    <span className="wl__levelName">{l.key}. {l.name}</span>
                                    <span className="wl__levelTone">{l.tone}</span>
                                </button>
                            ))}
                        </div>
                    </section>

                    <section className="wl__card">
                        <h2 className="wl__cardTitle">Figures</h2>
                        <div className="wl__row">
                            <div className="wl__field">
                                <label className="wl__label" htmlFor="wl-amount">Amount owed (AED)</label>
                                <input id="wl-amount" className="wl__input" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
                            </div>
                            <div className="wl__field">
                                <label className="wl__label" htmlFor="wl-since">Oldest invoice overdue since</label>
                                <input id="wl-since" className="wl__input" type="date" max={todayDubaiYMD()} value={overdueSince} onChange={(e) => setOverdueSince(e.target.value)} />
                            </div>
                        </div>
                        <div className="wl__row">
                            <div className="wl__field">
                                <label className="wl__label" htmlFor="wl-date">Letter date</label>
                                <input id="wl-date" className="wl__input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                            </div>
                            <div className="wl__field">
                                <label className="wl__label" htmlFor="wl-deadline">Pay by</label>
                                <input id="wl-deadline" className="wl__input" type="date" min={date} value={deadline} onChange={(e) => { setDeadline(e.target.value); setDeadlineTouched(true); }} />
                            </div>
                        </div>
                    </section>

                    <section className="wl__card">
                        <h2 className="wl__cardTitle">Addressed to</h2>
                        <div className="wl__field">
                            <label className="wl__label" htmlFor="wl-to">{prefill?.client.is_business === false ? 'Name' : 'Company'}</label>
                            <input id="wl-to" className="wl__input" type="text" value={addressee} onChange={(e) => setAddressee(e.target.value)} />
                        </div>
                        <div className="wl__row">
                            <div className="wl__field">
                                <label className="wl__label" htmlFor="wl-attn">Contact person (Attn)</label>
                                <input id="wl-attn" className="wl__input" type="text" placeholder="Optional" value={attention} onChange={(e) => setAttention(e.target.value)} />
                            </div>
                            <div className="wl__field">
                                <label className="wl__label" htmlFor="wl-dear">Greeting</label>
                                <input id="wl-dear" className="wl__input" type="text" value={salutation} onChange={(e) => setSalutation(e.target.value)} />
                            </div>
                        </div>
                    </section>

                    <section className="wl__card">
                        <h2 className="wl__cardTitle">Bed linen still in the unit</h2>
                        <p className="wl__hint">Leave everything at 0 and the letter does not mention linen.</p>
                        <div className="wl__row wl__row--3">
                            {(prefill?.linen_prices || [{ key: 'single', label: 'Single set', price: 290.85 }, { key: 'double', label: 'Double set', price: 367.5 }, { key: 'super_king', label: 'Super King set', price: 399 }]).map((l) => (
                                <div className="wl__field" key={l.key}>
                                    <label className="wl__label" htmlFor={`wl-linen-${l.key}`}>{l.label}</label>
                                    <input id={`wl-linen-${l.key}`} className="wl__input" type="number" min="0" step="1" placeholder="0" value={linen[l.key]} onChange={(e) => setLinen((prev) => ({ ...prev, [l.key]: e.target.value }))} />
                                    <span className="wl__hint">{money(l.price)} incl. VAT</span>
                                </div>
                            ))}
                        </div>
                        {linenTotal > 0 && <div className="wl__linenTotal">If not returned: <strong>{money(linenTotal)}</strong> incl. VAT added to the pending invoices</div>}
                    </section>

                    <section className="wl__card">
                        <h2 className="wl__cardTitle">Letter options</h2>
                        <div className="wl__toggles">
                            <label className="wl__toggle"><input type="checkbox" checked={withLetterhead} onChange={(e) => setWithLetterhead(e.target.checked)} /> Company letterhead</label>
                            <label className="wl__toggle"><input type="checkbox" checked={withSignature} onChange={(e) => setWithSignature(e.target.checked)} /> Signature and stamp</label>
                            <label className="wl__toggle"><input type="checkbox" checked={includeQr && prefill?.has_pay_link !== false} onChange={(e) => setIncludeQr(e.target.checked)} disabled={prefill?.has_pay_link === false} /> Payment QR code</label>
                        </div>
                        <div className="wl__row wl__gapTop">
                            <div className="wl__field">
                                <label className="wl__label" htmlFor="wl-sign">Signed by</label>
                                <input id="wl-sign" className="wl__input" type="text" value={signatoryName} onChange={(e) => setSignatoryName(e.target.value)} />
                            </div>
                            <div className="wl__field">
                                <label className="wl__label" htmlFor="wl-title">Title</label>
                                <input id="wl-title" className="wl__input" type="text" placeholder="Manager" value={signatoryTitle} onChange={(e) => setSignatoryTitle(e.target.value)} />
                            </div>
                        </div>
                    </section>

                    <section className="wl__card">
                        <button type="button" className="wl__disclosure" onClick={() => setWordingOpen((o) => !o)} aria-expanded={wordingOpen}>
                            <span className="wl__cardTitle wl__cardTitle--inline">Wording</span>
                            <span className="wl__muted">{edited ? 'edited by hand' : 'standard text'} · {wordingOpen ? 'hide' : 'edit'}</span>
                        </button>
                        {wordingOpen && (
                            <>
                                <div className="wl__field wl__gapTop">
                                    <label className="wl__label" htmlFor="wl-subject">Subject</label>
                                    <input id="wl-subject" className="wl__input" type="text" maxLength={160} value={subject} onChange={(e) => { setSubject(e.target.value); setEdited(true); }} />
                                </div>
                                <div className="wl__field">
                                    <label className="wl__label" htmlFor="wl-body">Letter text</label>
                                    <textarea id="wl-body" className="wl__input wl__textarea" rows={14} value={body} onChange={(e) => { setBody(e.target.value); setEdited(true); }} />
                                    <span className="wl__hint">Blank line = new paragraph · **bold** · lines starting with “- ” become bullets. Edited text no longer follows the figures above.</span>
                                </div>
                                {edited && <button type="button" className="wl__link" onClick={resetWording}>Back to the standard text</button>}
                            </>
                        )}
                    </section>
                </div>

                <div className="wl__preview">
                    <div className="wl__previewBar">
                        <span className="wl__previewTitle">
                            Preview {previewBusy && <span className="wl__muted">· updating…</span>}
                        </span>
                        <div className="wl__actions">
                            <button type="button" className="wl__btn wl__btn--ghost" onClick={() => generate({ print: true })} disabled={!ready || downloading || !!previewError}>
                                Print
                            </button>
                            <button type="button" className="wl__btn wl__btn--ghost" onClick={() => generate()} disabled={!ready || downloading || !!previewError}>
                                {downloading ? 'Generating…' : 'Download PDF'}
                            </button>
                            <button type="button" className="wl__btn wl__btn--done" onClick={markPrinted} disabled={!ready || marking || !!previewError} title="Adds this letter to the letter log, awaiting delivery">
                                {marking ? 'Saving…' : 'Mark as printed'}
                            </button>
                        </div>
                    </div>
                    <p className="wl__hint wl__previewHint">Printing or downloading logs nothing. Press “Mark as printed” once the letter is on paper — that adds it to the letter log.</p>
                    {previewError && <div className="wl__error">{previewError}</div>}
                    <div className="wl__paper">
                        {previewUrl && ready ? (
                            <iframe title="Letter preview" className="wl__frame" src={`${previewUrl}#toolbar=0&navpanes=0&view=FitH`} />
                        ) : (
                            <div className="wl__paperEmpty">
                                {clientOpt ? (prefillLoading ? 'Reading the client’s figures…' : 'Enter the amount owed to see the letter.') : 'Pick a client to start.'}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {editingDelivery && (
                <DeliveryDialog
                    letter={editingDelivery}
                    onClose={() => setEditingDelivery(null)}
                    onSaved={() => { refreshHistory(); refreshAwaiting(); }}
                />
            )}
            {archiving && (
                <ArchiveDialog
                    letter={archiving}
                    onClose={() => setArchiving(null)}
                    onSaved={() => { refreshHistory(); refreshAwaiting(); }}
                />
            )}
        </div>
    );
}
